import React, { cloneElement, ReactElement, ReactNode, useState } from "react";
import styled from "styled-components";
import { LayoutProps, layout, PositionProps, position } from "styled-system";
import css from "@styled-system/css";

import { ModalProps } from "../Modal/modal.type";

import Modal from "../Modal/Modal";
import BaseButton from "../Button/BaseButton";
import Icon from "../Icon/Icon";

export interface VideoOverlayProps
  extends Omit<ModalProps, "showXButton" | "isOpen" | "onClose"> {
  videoId: string;
  videoTitle: string;
  children: ReactNode;
}

const StyledModal = styled(Modal)<LayoutProps>`
  ${css({
    backgroundColor: "background-dark-color",
    borderRadius: 2,
    p: 0,
  })}
  ${layout}
`;

const CloseButton = styled(BaseButton)<PositionProps>`
  ${css({
    backgroundColor: "background-dark-color",
    borderRadius: "100%",
  })}
  ${position}
`;

const StyledIframe = styled.iframe`
  ${css({ border: "none", borderRadius: 2 })}
`;

const VideoOverlay = ({
  videoId,
  videoTitle,
  children,
  isDismissable = true,
  overlayVariant = "dark-blurred",
  ...props
}: VideoOverlayProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const triggerElement =
    typeof children === "function"
      ? children(setIsOpen)
      : cloneElement(children as ReactElement, {
          onClick: () => setIsOpen(true),
        });

  const closeModal = () => setIsOpen(false);

  return (
    <>
      {triggerElement}
      {isOpen && (
        <StyledModal
          isOpen={isOpen}
          onClose={closeModal}
          showXButton={false}
          isDismissable={isDismissable}
          overlayVariant={overlayVariant}
          maxWidth={876}
          width={{ _: "calc(100vw - 40px)", desktop: "calc(100vw - 140px)" }}
          height={{
            _: 200,
            tablet: 412,
            desktop: 496,
          }}
          {...props}
        >
          <CloseButton
            onClick={closeModal}
            width="60px"
            height="60px"
            position="absolute"
            top={{ _: "-80px", desktop: "-60px" }}
            right={{ _: 0, desktop: "-60px" }}
            aria-label={`Close ${videoTitle} video`}
          >
            <Icon
              name="times"
              weight="light"
              color="primary-white"
              width={40}
              height={40}
            />
          </CloseButton>
          <StyledIframe
            id="ytplayer"
            title={videoTitle}
            src={`https://youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&fs=1`}
            width="100%"
            height="100%"
            allowFullScreen={true}
            frameBorder="0"
            allow="autoplay"
          ></StyledIframe>
        </StyledModal>
      )}
    </>
  );
};

export default VideoOverlay;
